import { router } from '@inertiajs/react'
import { Link } from '@/Components/Navigation/Link'
import { ArrowLeftIcon, ChevronDownIcon } from '@heroicons/react/16/solid'
import { NavigationList } from './NavigationList'
import { ChangeClientUrl, Navigation } from '@/types'
import { useRef, useState } from 'react'
import { Combobox, ComboboxButton, ComboboxInput, ComboboxOption, ComboboxOptions } from '@headlessui/react'
import { TaxConsultantClient } from '@/types/models'
import { classNames, highlightMatches } from '@/Utils/Utils'

interface ClientNavigationSectionProps {
    changeClientUrl: ChangeClientUrl
    navigation: Navigation
}

export function ClientNavigationSection({ navigation, changeClientUrl }: ClientNavigationSectionProps) {
    const [query, setQuery] = useState('')
    const comboInputRef = useRef<HTMLInputElement>(null)
    const buttonRef = useRef<HTMLButtonElement>(null)

    const filteredClients =
        query === ''
            ? navigation.pageProps.otherClients
            : navigation.pageProps.otherClients.filter((client: TaxConsultantClient) => {
                  return client.name.toLowerCase().includes(query.toLowerCase())
              })

    function handleSelectedClient(client: TaxConsultantClient | null) {
        if (!client) {
            return
        }

        router.get(
            changeClientUrl(client.id),
            {},
            {
                onSuccess: () => {
                    if (buttonRef.current) {
                        buttonRef.current.focus()
                    }
                },
            }
        )
    }

    let handleSetInputActive = () => {
        setQuery('')
        if (comboInputRef.current) {
            comboInputRef.current.value = ''
        }

        const isOpen = buttonRef.current?.getAttribute('aria-expanded') === 'true'
        if (!isOpen) {
            buttonRef.current?.click()
        }
    }
    return (
        <>
            <li className="mb-2 border-t border-b border-stone-200 pt-1 pb-2">
                <Combobox as="div" value={navigation.pageProps.currentClient} onChange={handleSelectedClient} className={'group'}>
                    <div className="relative">
                        <ComboboxInput
                            ref={comboInputRef}
                            aria-label="Mandant*in"
                            className="z-10 -ml-1 w-[calc(100%+0.5rem)] cursor-pointer truncate rounded border-transparent bg-transparent py-1 pr-7 pl-1 font-semibold tracking-wide text-stone-900 shadow-none transition group-hover:border group-hover:border-stone-300 group-hover:bg-stone-50 group-data-open:cursor-text group-data-open:border-stone-300 group-data-open:bg-stone-50 focus:ring-0 focus:outline-0 active:ring-0"
                            onChange={(event) => setQuery(event.target.value)}
                            onClick={handleSetInputActive}
                            onBlur={() => setQuery('')}
                            displayValue={(client: TaxConsultantClient) => client?.name}
                        />
                        <ComboboxButton
                            ref={buttonRef}
                            onClick={handleSetInputActive}
                            className="absolute inset-y-0 right-0 flex items-center rounded-r-md focus:outline-hidden"
                        >
                            <ChevronDownIcon className="size-5 text-stone-400" aria-hidden="true" />
                        </ComboboxButton>

                        <ComboboxOptions
                            transition
                            className="absolute z-10 max-h-128 w-full overflow-auto rounded-b-md bg-white py-1 text-base shadow-lg outline outline-black/5 data-leave:transition data-leave:duration-100 data-leave:ease-in data-closed:data-leave:opacity-0 sm:text-sm"
                        >
                            {filteredClients.map((client) => (
                                <ComboboxOption
                                    key={client.id}
                                    value={client}
                                    className={classNames(
                                        'px-3 py-2 select-none',
                                        navigation.pageProps.currentClient?.id === client.id
                                            ? 'bg-stone-400 text-white'
                                            : 'cursor-pointer text-stone-900 data-focus:bg-stone-600 data-focus:text-white data-focus:outline-hidden'
                                    )}
                                >
                                    <span className="block truncate">{highlightMatches(client.name, query)}</span>
                                </ComboboxOption>
                            ))}
                        </ComboboxOptions>
                    </div>
                </Combobox>

                <Link
                    className="flex items-center gap-2 text-sm font-semibold tracking-wide text-stone-600 hover:text-stone-800"
                    href={route('dashboard')}
                >
                    <ArrowLeftIcon className="h-4 w-4" />
                    Alle Mandanten
                </Link>
            </li>
            <NavigationList items={navigation.sections.client} />
        </>
    )
}
